// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  Tooltip,
  Typography,
  type ListItemProps,
} from '@mui/material';
import { DocumentEntityType } from '@shared/data-access/types/engine-types';
import { AppIcon } from '@shared/ui-kit/components/app-icon';
// eslint-disable-next-line @nx/enforce-module-boundaries -- entity drawer embeds document-management; cycle with documents page Loader import
import { EntityDocumentsTab } from '@shared/document-management/components/entity-documents-tab';
import { Table, Tabs } from '@shared/ui-kit';
import { Translation } from '@shared/ui-kit/providers/translation-provider';
import { classNames } from '@shared/ui-kit/utils/classnames';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import type { PropsWithChildren, ReactNode } from 'react';
import {
  CREATED_AT_FORMAT_OPTIONS,
  useTransactionDrawer,
} from './use-transaction-drawer';
import styles from './transaction-drawer.module.css';

const DetailField = ({ label, value }: { label: ReactNode; value: ReactNode }) => (
  <>
    <dt className={styles['info-term']}>
      <span className={styles['info-label']}>{label}</span>
    </dt>
    <dd className={styles['info-value']}>{value}</dd>
  </>
);

export const TransactionDrawer = defineComponent({
  displayName: 'TransactionDrawer',
  useSetup: useTransactionDrawer,
  Slots: {
    SectionTitle: ({ children }: PropsWithChildren) => (
      <Typography variant="subtitle2" className={styles['section-title']}>
        {children}
      </Typography>
    ),
    Section: ({
      divider,
      className,
      ...props
    }: ListItemProps & { divider?: boolean }) => (
      <>
        <ListItem
          className={classNames(styles.section, className)}
          disablePadding
          disableGutters
          {...props}
        >
          {props.children}
        </ListItem>
        {divider && <Divider />}
      </>
    ),
  },
  View: ({
    DrawerRenderer,
    onClose,
    Slots,
    transaction = null,
    maskedIban,
    headerTitle,
    description,
    isAmountPositive,
    selectedCurrency,
    getTypeLabel,
    getSubtypeLabel,
    t,
  }) => (
    <DrawerRenderer
      anchor="right"
      PaperProps={{
        classes: {
          root: styles['drawer-paper'],
        },
      }}
    >
      <Tabs.Root
        defaultValue="info"
        baseId="transaction-detail-tabs"
        variant="pill"
        className={styles['drawer-tabs']}
      >
        <List dense disablePadding className={styles['drawer-list']}>
          <Slots.Section
            divider
            className={classNames(
              styles['section-sticky-top'],
              styles['section-header'],
            )}
          >
            <header className={styles['header-row']}>
              <div className={styles['icon-wrap']}>
                <AppIcon icon="Receipt" color="disabled" size="lg" />
              </div>

              <div className={styles['header-content']}>
                <Typography variant="body2" className={styles['institution-name']}>
                  {transaction?.institution_name ?? '-'}
                </Typography>

                <Tooltip title={headerTitle} placement="top">
                  <span className="inline-block min-w-0 max-w-full truncate text-xl font-semibold text-title-dark">
                    {headerTitle}
                  </span>
                </Tooltip>
              </div>
            </header>

            <div className={styles['header-close-wrap']}>
              <IconButton
                aria-label={t('common.close', { ns: 'common' })}
                size="small"
                className={styles['close-button']}
                onClick={onClose}
                data-testid="transaction-drawer-close"
              >
                <AppIcon icon="Close" color="inherit" />
              </IconButton>
            </div>

            <div className={styles['summary-block']}>
              <div className={styles['summary-row']}>
                <Typography
                  variant="body2"
                  className={classNames(styles['masked-iban'], 'money-display')}
                >
                  {maskedIban}
                </Typography>

                <Typography
                  variant="h6"
                  className={
                    isAmountPositive
                      ? styles['amount-positive']
                      : styles['amount-negative']
                  }
                >
                  <Table.Data
                    format="currency"
                    validate
                    value={transaction?.amount}
                    currency={selectedCurrency}
                    safeValue="-"
                  />
                </Typography>
              </div>

              <Tabs.List>
                <Tabs.Trigger value="info">
                  <Translation
                    i18nKey="transaction-drawer.tabs.info"
                    ns="components"
                  />
                </Tabs.Trigger>
                <Tabs.Trigger value="docs">
                  <Translation
                    i18nKey="transaction-drawer.tabs.docs"
                    ns="components"
                  />
                </Tabs.Trigger>
              </Tabs.List>
            </div>
          </Slots.Section>

          <Slots.Section>
            <Tabs.Content value="info" container={false} className="w-full">
              <div className="flex w-full flex-col gap-4">
                <div className={styles['section-body']}>
                  <Slots.SectionTitle>
                    <Translation
                      i18nKey="aggregator.create-bank.transactions.section.movement"
                      ns="aggregator"
                    />
                  </Slots.SectionTitle>

                  <dl className={styles['info-grid']}>
                    <DetailField
                      label={<Translation i18nKey="common.date" ns="common" />}
                      value={
                        <Table.Data
                          format="date"
                          value={transaction?.date}
                          safeValue="-"
                          container={false}
                        />
                      }
                    />

                    <DetailField
                      label={<Translation i18nKey="common.type" ns="common" />}
                      value={getTypeLabel(transaction?.type)}
                    />

                    <DetailField
                      label={<Translation i18nKey="common.category" ns="common" />}
                      value={getSubtypeLabel(transaction?.subtype)}
                    />

                    <DetailField
                      label={<Translation i18nKey="common.currency" ns="common" />}
                      value={transaction?.currency ?? '-'}
                    />

                    <DetailField
                      label={<Translation i18nKey="common.amount" ns="common" />}
                      value={
                        <Table.Data
                          format="currency"
                          validate
                          value={transaction?.amount}
                          currency={selectedCurrency}
                          safeValue="-"
                          container={false}
                        />
                      }
                    />

                    <DetailField
                      label={
                        <Translation
                          i18nKey="aggregator.create-bank.transactions.name"
                          ns="aggregator"
                        />
                      }
                      value={
                        transaction?.name ? (
                          <Tooltip title={transaction.name} placement="top">
                            <span className="ml-auto block max-w-xs truncate text-right text-base font-medium text-title-dark">
                              {transaction.name}
                            </span>
                          </Tooltip>
                        ) : (
                          '-'
                        )
                      }
                    />

                    {description ? (
                      <div className={styles['description-block']}>
                        <span className={styles['description-label']}>
                          <Translation i18nKey="common.description" ns="common" />
                        </span>
                        <Tooltip title={description} placement="top">
                          <span className="block w-full truncate text-base text-title-dark">
                            {description}
                          </span>
                        </Tooltip>
                      </div>
                    ) : (
                      <DetailField
                        label={
                          <Translation i18nKey="common.description" ns="common" />
                        }
                        value="-"
                      />
                    )}
                  </dl>
                </div>

                <Divider />

                <div className={styles['section-body']}>
                  <Slots.SectionTitle>
                    <Translation
                      i18nKey="aggregator.create-bank.transactions.section.additional-data"
                      ns="aggregator"
                    />
                  </Slots.SectionTitle>

                  <dl className={styles['info-grid']}>
                    <DetailField
                      label={<Translation i18nKey="common.ticker" ns="common" />}
                      value={transaction?.ticker ?? '-'}
                    />

                    <DetailField
                      label={
                        <Translation
                          i18nKey="aggregator.create-bank.transactions.isin"
                          ns="aggregator"
                        />
                      }
                      value={transaction?.isin ?? '-'}
                    />

                    <DetailField
                      label={
                        <Translation
                          i18nKey="aggregator.create-bank.transactions.asset-type"
                          ns="aggregator"
                        />
                      }
                      value={transaction?.asset_type ?? '-'}
                    />

                    <DetailField
                      label={<Translation i18nKey="common.units" ns="common" />}
                      value={
                        <Table.Data
                          format="decimal"
                          value={transaction?.quantity}
                          safeValue="-"
                          container={false}
                        />
                      }
                    />

                    <DetailField
                      label={<Translation i18nKey="common.price" ns="common" />}
                      value={
                        <Table.Data
                          format="currency"
                          value={transaction?.price}
                          currency={selectedCurrency}
                          safeValue="-"
                          container={false}
                        />
                      }
                    />

                    <DetailField
                      label={
                        <Translation
                          i18nKey="aggregator.transaction-details.local-price"
                          ns="aggregator"
                        />
                      }
                      value={
                        <Table.Data
                          format="currency"
                          value={transaction?.local_price}
                          currency={transaction?.currency ?? ''}
                          safeValue="-"
                          container={false}
                        />
                      }
                    />
                  </dl>
                </div>

                <Divider />

                <div className={styles['section-body']}>
                  <Slots.SectionTitle>
                    <Translation
                      i18nKey="aggregator.transaction-details.section.record"
                      ns="aggregator"
                    />
                  </Slots.SectionTitle>

                  <dl className={styles['info-grid']}>
                    <DetailField
                      label={
                        <Translation
                          i18nKey="aggregator.transaction-details.created-at"
                          ns="aggregator"
                        />
                      }
                      value={
                        <Table.Data
                          format="date"
                          value={transaction?.created_at}
                          safeValue="-"
                          formatOptions={CREATED_AT_FORMAT_OPTIONS}
                          container={false}
                        />
                      }
                    />
                  </dl>
                </div>
              </div>
            </Tabs.Content>

            <Tabs.Content value="docs" container={false} className="w-full">
              <EntityDocumentsTab
                entityType={DocumentEntityType.Transaction}
                entityId={transaction?.id ?? ''}
                disabled={!transaction?.id}
              />
            </Tabs.Content>
          </Slots.Section>

          <Slots.Section className={styles['section-spacer']} />

          <Slots.Section className={styles['section-sticky-bottom']}>
            <div className={styles['footer-actions']}>
              <Button
                type="button"
                variant="contained"
                color="secondary"
                fullWidth
                onClick={onClose}
                data-testid="transaction-drawer-footer-close"
              >
                <Translation i18nKey="common.close" ns="common" />
              </Button>
            </div>
          </Slots.Section>
        </List>
      </Tabs.Root>
    </DrawerRenderer>
  ),
});
